import React from 'react';

import BugReportIcon from '@mui/icons-material/BugReport';
import FeedbackIcon from '@mui/icons-material/Feedback';
import SettingsIcon from '@mui/icons-material/Settings';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Tooltip from '@mui/material/Tooltip';

import ToolbarPopover from 'components/layout/ToolbarPopover';
import { NOTICE_TYPE_KEY, createAdminNotice } from 'stores/NoticeAdminStoreAdapter';
import { getUser } from 'stores/UserStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import BugReportDialog from './BugReportDialog';
import FeedbackDialog from './FeedbackDialog';

/**
 * DEVELOPERロールを持つユーザーのIDリストを取得する。
 */
const getDeveloperUserIds = async (): Promise<string[]> => {
  const users = await getUser({});
  return users.filter((u) => u.roles.includes('DEVELOPER')).map((u) => u.userId);
};

type Props = {
  userName: string;
};

const SettingsMenu: React.FC<Props> = ({ userName }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [bugReportOpen, setBugReportOpen] = React.useState(false);
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const menuOpen = Boolean(anchorEl);

  const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleBugReportClick = () => {
    handleMenuClose();
    setBugReportOpen(true);
  };

  const handleFeedbackClick = () => {
    handleMenuClose();
    setFeedbackOpen(true);
  };

  const handleBugReportSubmit = async (screen: string, description: string) => {
    setLoading(true);
    try {
      const developerUserIds = await getDeveloperUserIds();
      if (developerUserIds.length === 0) {
        errorDefault('通知先のユーザーが見つかりませんでした');
        return;
      }
      await createAdminNotice({
        noticeType: NOTICE_TYPE_KEY.NOTICE,
        title: `${screen}の不具合報告がありました。`,
        content: `${description}\n送信者:${userName}`,
        priority: 1,
        userIds: developerUserIds,
      });
      successDefault('不具合報告を送信しました');
      setBugReportOpen(false);
    } catch (e) {
      errorDefault('不具合報告の送信に失敗しました', toErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  const handleFeedbackSubmit = async (content: string) => {
    setLoading(true);
    try {
      const developerUserIds = await getDeveloperUserIds();
      if (developerUserIds.length === 0) {
        errorDefault('通知先のユーザーが見つかりませんでした');
        return;
      }
      await createAdminNotice({
        noticeType: NOTICE_TYPE_KEY.NOTICE,
        title: `${userName}からのフィードバックがありました。`,
        content: `${content}\n送信者:${userName}`,
        priority: 5,
        userIds: developerUserIds,
      });
      successDefault('フィードバックを送信しました');
      setFeedbackOpen(false);
    } catch (e) {
      errorDefault('フィードバックの送信に失敗しました', toErrorDetail(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="設定">
        <IconButton size="small" onClick={handleIconClick}>
          <SettingsIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        </IconButton>
      </Tooltip>

      <ToolbarPopover open={menuOpen} anchorEl={anchorEl} onClose={handleMenuClose} width={220}>
        <MenuList dense>
          <MenuItem onClick={handleBugReportClick}>
            <ListItemIcon>
              <BugReportIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>不具合報告</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleFeedbackClick}>
            <ListItemIcon>
              <FeedbackIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>フィードバック</ListItemText>
          </MenuItem>
        </MenuList>
      </ToolbarPopover>

      <BugReportDialog
        open={bugReportOpen}
        onClose={() => setBugReportOpen(false)}
        onSubmit={handleBugReportSubmit}
        loading={loading}
      />

      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        onSubmit={handleFeedbackSubmit}
        loading={loading}
      />
    </>
  );
};

export default SettingsMenu;
